import * as React from 'react';
import { Card, CardContent } from '@/hen-ui/composites/Card';
import { Image } from '@/hen-ui/atomics/Image';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';

export type InfoCardProps = {
  /** Optional image source URL */
  imageSrc?: string;
  /** Optional alt text for the image (defaults to title-based alt if title provided) */
  imageAlt?: string;
  /** Card title text */
  title?: string;
  /** Card description text or React node */
  children?: React.ReactNode;
  /** Optional className for the card */
  className?: string;
};

/**
 * InfoCard - Composite component for information cards
 *
 * A composite component that displays information in a card format with optional image,
 * title, and description. Uses Card and CardContent composites with atomic components
 * (Image, Heading, Text) following HEN Design System standards.
 *
 * Uses info-card spacing tokens (px-info-card-sides, py-info-card-top-bottom) for
 * consistent padding across info card layouts.
 *
 * @example
 * ```tsx
 * <InfoCard
 *   imageSrc="/path/to/image.png"
 *   imageAlt="Card image"
 *   title="Card Title"
 *   description="Card description text"
 * />
 * ```
 */
export const InfoCard: React.FC<InfoCardProps> = ({
  imageSrc,
  imageAlt,
  title,
  children,
  className,
}) => {
  const defaultImageAlt = title ? `${title} icon` : 'Card image';

  return (
    <Card className={`${className || ''}`}>
      <CardContent className='flex flex-col px-info-card-sides py-info-card-top-bottom text-left gap-sm'>
        {imageSrc && (
          <Image
            src={imageSrc}
            alt={imageAlt || defaultImageAlt}
            className='w-16 h-16'
          />
        )}
        {title && <Heading variant='h3'>{title}</Heading>}
        {children && (
          <Text variant='b4' className='text-body'>
            {children}
          </Text>
        )}
      </CardContent>
    </Card>
  );
};

InfoCard.displayName = 'InfoCard';
